import { sum } from 'lodash';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { useReportError } from '@/hooks/useReportError';
import { useReviewSuggestionCountsQuery } from '@/modules/aiSuggestions/reviewSuggestions/ReviewSuggestions.generated';
import { getReviewSuggestionCounts } from '@/modules/aiSuggestions/reviewSuggestions/ReviewSuggestions.utils';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { getNodes } from '@/utils/graphqlUtils';

export function useSetupReviewSuggestions({
  householdId,
  enabled,
  running,
  reviewOpen,
}: {
  householdId: string;
  enabled: boolean;
  running: boolean;
  reviewOpen: boolean;
}) {
  const household = useUnguardedHouseholdDetailsContext();
  const previousRunning = usePrevious(running);
  const previousReviewOpen = usePrevious(reviewOpen);
  const needsRefresh =
    (previousRunning && !running) || (previousReviewOpen && !reviewOpen);
  const { data, loading, error, refetch } = useReviewSuggestionCountsQuery({
    variables: { householdId },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    // The household workflow provider refetches this active query as roots finish.
  });
  const { reportError } = useReportError(
    'Failed to read setup suggestions',
    error
  );
  useEffect(() => {
    if (enabled && needsRefresh) {
      void refetch().catch((error: unknown) =>
        reportError(
          'Failed to refresh setup suggestions',
          error instanceof Error ? error : new Error(String(error))
        )
      );
    }
  }, [enabled, needsRefresh, refetch, reportError]);

  if (!enabled || !data || error) return undefined;
  const count = sum(
    Object.values(
      getReviewSuggestionCounts(
        getNodes(data.aiSuggestions),
        household?.defaultFirstToDieClientId ?? undefined
      )
    )
  );
  // Cached suggestions remain actionable, but stale zero must not imply completion.
  if (count === 0 && (loading || needsRefresh)) return undefined;
  return count;
}
